import { GoogleGenerativeAI, SchemaType } from "@google/generative-ai";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ExtractedEvent {
  name?: string | null;
  event_date?: string | null;
  event_time?: string | null;
  location?: string | null;
  event_type?: string[] | null;
  expected_attendees?: number | null;
  budget_estimate?: number | null;
  internal_notes?: string | null;
}

export interface ExtractedOrganizer {
  name?: string | null;
  category?: string | null;
  contact_person?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
}

export interface ExtractedMember {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  role_in_team?: string | null;
  instrument?: string | null;
}

export interface ExtractedTeam {
  name?: string | null;
  category?: string | null;
  description?: string | null;
  member_emails?: string[] | null;
}

export interface AIResponse {
  event?: ExtractedEvent;
  organizer?: ExtractedOrganizer;
  existing_organizer_match?: string | null;
  members?: ExtractedMember[];
  existing_member_matches?: string | null; // JSON string
  team?: ExtractedTeam;
  existing_team_match?: string | null;
  missing_info: string[];
  ready_to_create: boolean;
  assistant_message: string;
}

export interface ExistingOrganizer {
  id: string;
  name: string;
}

export interface ExistingMember {
  id: string;
  full_name: string;
  email: string;
}

export interface ExistingTeam {
  id: string;
  name: string;
}

export interface ExistingEvent {
  id: string;
  name: string;
  event_date: string;
  location: string;
  organizer_name?: string;
}

const extractionSchema = {
  type: SchemaType.OBJECT,
  properties: {
    event: {
      type: SchemaType.OBJECT,
      properties: {
        name: {
          type: SchemaType.STRING,
          description:
            "Nom de l'événement (ex: Messe de Pâques, Concert de Noël)",
          nullable: true,
        },
        event_date: {
          type: SchemaType.STRING,
          description: "Date de l'événement au format YYYY-MM-DD",
          nullable: true,
        },
        event_time: {
          type: SchemaType.STRING,
          description: "Heure de l'événement au format HH:MM",
          nullable: true,
        },
        location: {
          type: SchemaType.STRING,
          description: "Lieu de l'événement (adresse ou nom du lieu)",
          nullable: true,
        },
        event_type: {
          type: SchemaType.ARRAY,
          items: { type: SchemaType.STRING },
          description:
            "Types d'événement parmi: Louange / Adoration, Évangélisation, Messe, Festival, Concert, Retraite, Conférence, Autre",
          nullable: true,
        },
        expected_attendees: {
          type: SchemaType.NUMBER,
          description: "Nombre de participants attendus",
          nullable: true,
        },
        budget_estimate: {
          type: SchemaType.NUMBER,
          description: "Budget estimé en euros",
          nullable: true,
        },
        internal_notes: {
          type: SchemaType.STRING,
          description: "Notes internes ou informations supplémentaires",
          nullable: true,
        },
      },
    },
    organizer: {
      type: SchemaType.OBJECT,
      properties: {
        name: {
          type: SchemaType.STRING,
          description:
            "Nom de l'organisation (paroisse, diocèse, association, etc.)",
          nullable: true,
        },
        category: {
          type: SchemaType.STRING,
          description:
            "Catégorie parmi: Diocese, Parish, Association, Festival, Institution",
          nullable: true,
        },
        contact_person: {
          type: SchemaType.STRING,
          description: "Nom de la personne de contact",
          nullable: true,
        },
        email: {
          type: SchemaType.STRING,
          description: "Email de contact",
          nullable: true,
        },
        phone: {
          type: SchemaType.STRING,
          description: "Numéro de téléphone",
          nullable: true,
        },
        address: {
          type: SchemaType.STRING,
          description: "Adresse de l'organisation",
          nullable: true,
        },
      },
    },
    existing_organizer_match: {
      type: SchemaType.STRING,
      description:
        "ID de l'organisateur existant si une correspondance est trouvée",
      nullable: true,
    },
    members: {
      type: SchemaType.ARRAY,
      items: {
        type: SchemaType.OBJECT,
        properties: {
          full_name: {
            type: SchemaType.STRING,
            description: "Nom complet du membre",
            nullable: true,
          },
          email: {
            type: SchemaType.STRING,
            description: "Email du membre",
            nullable: true,
          },
          phone: {
            type: SchemaType.STRING,
            description: "Téléphone du membre",
            nullable: true,
          },
          role: {
            type: SchemaType.STRING,
            description:
              "Rôle système parmi: musician, coordinator, director, admin",
            nullable: true,
          },
          role_in_team: {
            type: SchemaType.STRING,
            description:
              "Rôle dans l'équipe parmi: Chef de chœur, Soliste, Choriste, Musicien, Technicien son, Technicien lumière",
            nullable: true,
          },
          instrument: {
            type: SchemaType.STRING,
            description:
              "Instrument ou pupitre (Piano, Guitare, Voix soprano, etc.)",
            nullable: true,
          },
        },
      },
      description: "Liste des membres à créer ou modifier",
      nullable: true,
    },
    existing_member_matches: {
      type: SchemaType.STRING,
      description:
        'JSON string avec map email -> ID pour les membres existants trouvés (ex: \'{"jean@email.com": "uuid-123"}\')',
      nullable: true,
    },
    team: {
      type: SchemaType.OBJECT,
      properties: {
        name: {
          type: SchemaType.STRING,
          description: "Nom de l'équipe",
          nullable: true,
        },
        category: {
          type: SchemaType.STRING,
          description:
            "Catégorie parmi: Beatitude Family 1, Beatitude Family 2, Beatitude Family 3, Professional Group, Mixed",
          nullable: true,
        },
        description: {
          type: SchemaType.STRING,
          description: "Description de l'équipe",
          nullable: true,
        },
        member_emails: {
          type: SchemaType.ARRAY,
          items: { type: SchemaType.STRING },
          description: "Liste des emails des membres de l'équipe",
          nullable: true,
        },
      },
      nullable: true,
    },
    existing_team_match: {
      type: SchemaType.STRING,
      description: "ID de l'équipe existante si une correspondance est trouvée",
      nullable: true,
    },
    missing_info: {
      type: SchemaType.ARRAY,
      items: { type: SchemaType.STRING },
      description: "Liste des informations manquantes importantes",
    },
    ready_to_create: {
      type: SchemaType.BOOLEAN,
      description:
        "True si suffisamment d'informations pour créer les entités demandées",
    },
    assistant_message: {
      type: SchemaType.STRING,
      description:
        "Message de l'assistant pour l'utilisateur (résumé des infos extraites, questions sur les infos manquantes, ou confirmation de création)",
    },
  },
  required: ["missing_info", "ready_to_create", "assistant_message"],
};

const buildSystemPrompt = (
  currentDate: string,
) => `Tu es un assistant interne pour le Collectif Béatitudes, une organisation musicale chrétienne qui gère des événements, équipes et membres.

DATE ACTUELLE: ${currentDate}
Utilise cette date pour interpréter les références temporelles relatives:
- "samedi prochain" → le prochain samedi après ${currentDate}
- "en mars" → mars de l'année en cours si on n'y est pas encore, sinon mars de l'année prochaine
- "le 15" sans mois → le 15 du mois en cours ou prochain selon le contexte
- "dans 2 semaines" → 14 jours après ${currentDate}

CONTEXTE: L'utilisateur (un membre de l'équipe CBM) te partage des informations. Ces infos peuvent concerner:
- Un événement potentiel (email reçu, notes d'appel, SMS)
- Des membres du collectif (musiciens, coordinateurs)
- Des équipes musicales (composition, affectations)

Tu dois EXTRAIRE les informations et les RÉSUMER pour l'utilisateur, PAS répondre à l'expéditeur original.

TON RÔLE:
- Analyser le contenu et en extraire les informations utiles
- Identifier s'il s'agit d'un événement, de membres ou d'une équipe
- Faire correspondre avec les données existantes (organisateurs, membres, équipes)
- Résumer ce que tu as compris à l'utilisateur
- Poser des questions pour compléter les infos manquantes
- Aider à créer/modifier les entités dans le système
- DÉTECTER les doublons potentiels en comparant avec les événements existants

GESTION MULTI-TOUR:
- À chaque message, CUMULE les informations déjà collectées avec les nouvelles
- Ne redemande JAMAIS une info déjà fournie dans un message précédent
- Dans ton résumé, liste TOUTES les infos collectées jusqu'ici (pas seulement les nouvelles)

DÉTECTION DE DOUBLONS:
- Compare le nouvel événement avec la liste des événements existants fournie
- Un doublon potentiel = même organisateur + date proche (±7 jours) + lieu similaire
- Si doublon détecté, AVERTIS l'utilisateur et demande confirmation avant de créer

MATCHING D'ORGANISATEURS:
- Compare le nom mentionné avec les organisateurs existants
- Accepte les variations: "Paroisse St Jean" = "Saint-Jean" = "St Jean de Lyon"
- Préfère un match existant à la création d'un nouvel organisateur

FORMAT DE RÉPONSE:
- Commence par un résumé de TOUTES les infos collectées
- Si doublon potentiel, ALERTE en premier
- Liste ce qui manque si nécessaire
- Pose UNE ou DEUX questions maximum pour avancer

RÈGLES D'EXTRACTION:

Pour les ÉVÉNEMENTS:
1. Dates → format YYYY-MM-DD (utilise la date actuelle pour les références relatives)
2. Heures → format HH:MM (24h)
3. Déduis le type d'événement selon le contexte
4. Infos MINIMALES: nom, date, lieu, organisateur

Pour les MEMBRES:
1. Email obligatoire pour identifier un membre
2. Rôle système: musician (par défaut), coordinator, director, admin
3. Rôle dans équipe: Chef de chœur, Soliste, Choriste, Musicien, Technicien son, Technicien lumière
4. Instrument: Piano, Guitare, Basse, Batterie, Violon, Voix soprano/alto/ténor/basse, etc.

Pour les ÉQUIPES:
1. Nom obligatoire
2. Catégorie: Beatitude Family 1/2/3, Professional Group, Mixed
3. Liste des membres avec leurs rôles et instruments
4. Faire correspondre les membres avec les membres existants par email

TYPES D'ÉVÉNEMENTS: Louange / Adoration, Évangélisation, Messe, Festival, Concert, Retraite, Conférence, Autre

CATÉGORIES D'ORGANISATEURS: Diocese, Parish, Association, Festival, Institution

Réponds en français, de manière concise et professionnelle. Tu parles à un collègue, pas au contact externe.`;

async function sendChatMessageDirect(
  messages: ChatMessage[],
  existingOrganizers: ExistingOrganizer[],
  existingMembers: ExistingMember[] = [],
  existingTeams: ExistingTeam[] = [],
  existingEvents: ExistingEvent[] = [],
): Promise<AIResponse> {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

  if (!apiKey) {
    throw new Error("Clé API Gemini non configurée (VITE_GEMINI_API_KEY)");
  }

  const genAI = new GoogleGenerativeAI(apiKey);

  const currentDate = new Date().toISOString().split("T")[0];

  const limitedOrganizers = existingOrganizers.slice(0, 50);
  const organizersList =
    limitedOrganizers.length > 0
      ? `\n\nORGANISATEURS EXISTANTS (utilise l'ID si correspondance, accepte les variations de noms):\n${limitedOrganizers.map((o) => `- ${o.name} (ID: ${o.id})`).join("\n")}`
      : "";

  const membersList =
    existingMembers.length > 0
      ? `\n\nMEMBRES EXISTANTS (utilise l'ID si correspondance par email):\n${existingMembers.map((m) => `- ${m.full_name} <${m.email}> (ID: ${m.id})`).join("\n")}`
      : "";

  const teamsList =
    existingTeams.length > 0
      ? `\n\nÉQUIPES EXISTANTES (utilise l'ID si correspondance):\n${existingTeams.map((t) => `- ${t.name} (ID: ${t.id})`).join("\n")}`
      : "";

  const limitedEvents = existingEvents.slice(0, 30);
  const eventsList =
    limitedEvents.length > 0
      ? `\n\nÉVÉNEMENTS EXISTANTS (vérifie les doublons potentiels):\n${limitedEvents.map((e) => `- ${e.name} | ${e.event_date} | ${e.location}${e.organizer_name ? ` | ${e.organizer_name}` : ""} (ID: ${e.id})`).join("\n")}`
      : "";

  const model = genAI.getGenerativeModel({
    model: "gemini-3-flash",
    systemInstruction:
      buildSystemPrompt(currentDate) +
      organizersList +
      membersList +
      teamsList +
      eventsList,
    generationConfig: {
      responseMimeType: "application/json",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      responseSchema: extractionSchema as any,
    },
  });

  // Filter messages: Gemini requires first message to be from user
  // Remove leading assistant messages from history
  const historyMessages = messages.slice(0, -1);
  const firstUserIndex = historyMessages.findIndex((m) => m.role === "user");
  const filteredHistory =
    firstUserIndex >= 0 ? historyMessages.slice(firstUserIndex) : [];

  const chatHistory = filteredHistory.map((msg) => ({
    role: msg.role === "user" ? "user" : ("model" as const),
    parts: [{ text: msg.content }],
  }));

  const chat = model.startChat({
    history: chatHistory,
  });

  const lastMessage = messages[messages.length - 1];
  const result = await chat.sendMessage(lastMessage.content);
  const response = result.response;
  const text = response.text();

  return JSON.parse(text);
}

async function sendChatMessageNetlify(
  messages: ChatMessage[],
  existingOrganizers: ExistingOrganizer[],
  existingMembers: ExistingMember[] = [],
  existingTeams: ExistingTeam[] = [],
  existingEvents: ExistingEvent[] = [],
): Promise<AIResponse> {
  const response = await fetch("/.netlify/functions/aiChat", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      messages,
      existingOrganizers,
      existingMembers,
      existingTeams,
      existingEvents,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Erreur lors de la communication avec l'IA");
  }

  return response.json();
}

export async function sendChatMessage(
  messages: ChatMessage[],
  existingOrganizers: ExistingOrganizer[],
  existingMembers: ExistingMember[] = [],
  existingTeams: ExistingTeam[] = [],
  existingEvents: ExistingEvent[] = [],
): Promise<AIResponse> {
  const useDirectApi = !!import.meta.env.VITE_GEMINI_API_KEY;

  if (useDirectApi) {
    return sendChatMessageDirect(
      messages,
      existingOrganizers,
      existingMembers,
      existingTeams,
      existingEvents,
    );
  }

  return sendChatMessageNetlify(
    messages,
    existingOrganizers,
    existingMembers,
    existingTeams,
    existingEvents,
  );
}
